import React, { useCallback } from 'react'
import * as Yup from 'yup'
import { Grid } from '@mui/material'
import { EditionModeContextProvider, EditionModes, useEditionMode } from '../../../utils/providers/editionModeContext'
import { AdGroupStatus, Inventory, useInventoryQuery } from '../../../__generated__/graphql'
import { FormikValues, Formik } from 'formik'
import SectionTitle from '../../../components/form/SectionTitle'
import TransformDisplayToEdition from '../../../components/form/TransformDisplayToEdition'
import HasPermission from '../../../components/permissions/HasPermission'
import { PageStructure } from '../../../utils/interfaces/PageStructure'
import { useParams } from 'react-router-dom'
import { InventoryRoute } from '../../routeParams'
import ErrorAlert from '../../../components/error/ErrorAlert'
import Loader from '../../../components/loader/Loader'
import DisplayedItem from '../../../components/form/DisplayedItem'
import { initialTouchedForAll } from '../../../utils/initialTouchedForAll'
import FormWithRouteLeavingGuard from '../../../components/form/FormWIthRouteLeavingGuard'
import { useCanEditInventory } from '../../../utils/hooks/inventory/useCanEditInventory'
import { Section } from '../../../components/layout/Section'

export interface InventoryInfosFormProps {
  inventory: Pick<Inventory, 'name' | 'status' | 'format'>
}

export interface InventoryInfosFormInterface extends FormikValues {
  name: string
  status: AdGroupStatus
}

export const InventoryInfosFormStructure: PageStructure = {
  title: {
    display: 'adgroup.sectionTitle',
    edit: 'adgroup.edit.sectionTitle',
  },
  button: {
    cancel: 'cancelAdgroupInfos',
    save: 'saveAdgroupInfos',
    edit: 'editAdgroupInfos',
  },
  input: {
    name: 'commons.name',
    status: 'commons.status',
    format: 'adgroup.format',
  },
}

export const InventoryInfosForm: React.FC<InventoryInfosFormProps> = ({ inventory }) => {
  const canEditInventory = useCanEditInventory()
  const structure = InventoryInfosFormStructure

  const [editionModeContext, setEditionModeContext] = useEditionMode()
  const initialValues: InventoryInfosFormInterface = {
    name: inventory.name,
    status: inventory.status,
  }

  const inventoryUpdateSchema = Yup.object().shape({
    name: Yup.string().min(3).required('commons.required'),
    status: Yup.boolean().required('commons.required'),
  })

  const editHandler = useCallback(
    (_values: InventoryInfosFormInterface) => {
      // console.log(values)
    },
    [inventory, setEditionModeContext]
  )

  return (
    <Formik
      enableReinitialize={true}
      initialValues={initialValues}
      onSubmit={editHandler}
      validationSchema={inventoryUpdateSchema}
      initialTouched={initialTouchedForAll({ ...initialValues })}
      validateOnMount={true}
    >
      {(formik) => (
        <FormWithRouteLeavingGuard>
          <Section sx={{ mb: 0 }}>
            <SectionTitle
              canSubmit={formik.isValid && formik.dirty}
              title={editionModeContext === EditionModes.Display ? structure.title.display : structure.title.edit}
              defaultButtons
              isFirst
            />
            <Grid container spacing={1}>
              <Grid size={6}>
                <TransformDisplayToEdition
                  inputType="input"
                  type={'text'}
                  name={'name'}
                  required={true}
                  label={structure.input.name}
                  text={inventory.name}
                  editable={canEditInventory}
                />
              </Grid>
              {/* <Grid item xs={6}>
              <TransformDisplayToEdition
                inputType={'switch'}
                name={'status'}
                required={true}
                label={structure.input.status}
                text={inventory.status}
                editable={canEditInventory}
              />
            </Grid> */}
              <HasPermission permissionRequired="banner" roleRequired="read">
                <Grid size={4}>
                  <DisplayedItem label={structure.input.format} name="format">
                    {inventory?.format}
                  </DisplayedItem>
                </Grid>
              </HasPermission>
            </Grid>
          </Section>
        </FormWithRouteLeavingGuard>
      )}
    </Formik>
  )
}

const InventorySummary: React.FC = () => {
  const { inventoryId } = useParams<InventoryRoute>()
  const { data: inventoryData, loading, error } = useInventoryQuery({ variables: { id: inventoryId } })

  if (loading) return <Loader />
  if (error) return <ErrorAlert message={error.message} />

  return (
    <>
      <EditionModeContextProvider>
        {inventoryData?.inventory && <InventoryInfosForm inventory={inventoryData?.inventory} />}
      </EditionModeContextProvider>
    </>
  )
}

export default InventorySummary
